import type { Params } from '@vimcar/mvvm/linkViewModel'
import type { ViewState } from '#components/Root/viewModel'
import type { Transitioner } from '#components/TransitionProvider'
import type { Nodes } from '#models/Nodes'

import * as mobx from 'mobx'
import PT from 'prop-types'

import type { NodeLink } from '../../models/Nodes/NodeLink'
import type { SelectNode } from '../../models/Nodes/SelectNode'

export type Props = {
  state: ViewState
  node?: SelectNode | null
  filterTerm?: string | null
}

export type ViewModel = {
  _searchTerm: string
  ownNode: SelectNode
  showOptions?: Array<NodeLink> | null
  showButtons: boolean
  showContinue: boolean
  showBack: boolean
  showCancel: boolean
  isValid: boolean
  useMultilineButtons: boolean

  _setPreselectedNode: (SelectNode) => void
  handleRadioChange: () => void
  handleClick: (string) => () => void
  handleBack: () => void
  handleNext: () => void
  handleSearchFormChange: (args: { term: string }) => void
}

type Context = {
  models: { nodes: Nodes }
  transitioner: Transitioner
}

const mkViewModel = (
  { disposer, registerAutorun }: Params,
  props: Props,
  { transitioner }: Context,
): ViewModel => {
  const vm = mobx.observable({
    _searchTerm: '',
    _setPreselectedNode: mobx.action((node) => {
      // @ts-expect-error@2339
      if (node && node.radios) {
        // @ts-expect-error@2339
        const preselected = node.options.find((option) => option.preSelected)
        if (preselected && preselected.link) {
          props.state.nextNode = preselected.link.id
        }
      }
    }),
    get ownNode() {
      return props.node || props.state.currentNode
    },
    get options() {
      return (
        vm.ownNode &&
        vm.ownNode.options &&
        vm.ownNode.options.length &&
        vm.ownNode.options.filter((option) =>
          option.matches(props.filterTerm || vm._searchTerm),
        )
      )
    },
    get showButtons() {
      return !props.node && (vm.showContinue || vm.showBack || vm.showCancel)
    },
    get showContinue() {
      return !vm.options || (vm.ownNode && vm.ownNode.radios)
    },
    get showBack() {
      return vm.ownNode && vm.ownNode.allowBack
    },
    get showCancel() {
      return vm.ownNode && vm.ownNode.allowCancel
    },
    get isValid() {
      return !!(!vm.showButtons || props.state.nextNode)
    },
    get useMultilineButtons() {
      return vm.ownNode.options.some((o) => o.subtext)
    },
    get nextNodeId() {
      return props.state.nextNode
    },

    handleRadioChange: mobx.action((event) => {
      // @ts-expect-error@2339
      props.state.nextNode = event.target.value
    }),
    handleClick: mobx.action((id) => () => {
      // @ts-expect-error@2345
      transitioner.goForward(id)
    }),
    handleNext: mobx.action(() => {
      transitioner.goForward(props.state.nextNode)
      vm._searchTerm = ''
    }),
    handleBack: mobx.action(() => {
      vm._searchTerm = ''
    }),
    handleSearchFormChange: mobx.action((term) => {
      // @ts-expect-error@2322
      vm._searchTerm = term
    }),
  })

  registerAutorun(() => {
    vm._setPreselectedNode(vm.ownNode)
  })

  disposer.add(
    mobx.reaction(
      () => {
        return vm.ownNode
      },
      (node) => {
        props.state.nextNode = null
        vm._setPreselectedNode(node)
      },
    ),
  )

  // @ts-expect-error@2322
  return vm
}

mkViewModel.contextTypes = {
  transitioner: PT.object.isRequired,
}

export default mkViewModel
